import { useState, useEffect, useCallback } from 'react';
import { CheckmarkIcon, XIcon } from './icons';

export interface ToastProps {
  type: 'success' | 'error';
  title: string;
  description?: string;
  visible: boolean;
  onDismiss: () => void;
}

export function Toast({ type, title, description, visible, onDismiss }: ToastProps) {
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);

  const startExit = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setMounted(false);
      setExiting(false);
      onDismiss();
    }, 200);
  }, [onDismiss]);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setExiting(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || !mounted) return;
    const t = setTimeout(startExit, 5000);
    return () => clearTimeout(t);
  }, [visible, mounted, startExit]);

  if (!mounted) return null;

  const bg = type === 'success' ? 'var(--t-toast-success-bg)' : 'var(--t-toast-error-bg)';

  return (
    <div
      className="absolute top-0 left-0 right-0 z-[25] px-3 pt-3"
      style={{ animation: exiting ? 'slideUpToastOut 0.2s ease-in forwards' : 'slideDownToast 0.3s ease-out both' }}
    >
      <div
        className="flex items-start gap-2.5 rounded-2xl px-4 py-3"
        style={{ background: bg, color: '#ffffff' }}
      >
        <span className="mt-0.5 shrink-0">
          {type === 'success' ? <CheckmarkIcon size={14} /> : <XIcon size={12} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium leading-tight">{title}</div>
          {description && (
            <div className="text-[12px] leading-snug mt-0.5 opacity-90">{description}</div>
          )}
        </div>
        <button
          onClick={startExit}
          className="shrink-0 opacity-80 hover:opacity-100 transition-opacity mt-0.5"
        >
          <XIcon size={10} />
        </button>
      </div>
    </div>
  );
}
